import React from "react";
import { Link } from "react-router-dom";
import { Shield, Cookie, FileText, Lock, AlertTriangle, Database, Users, ServerCog, KeyRound } from "lucide-react";

const PILLARS = [
  {
    icon: FileText,
    title: "Privacy",
    desc: "What data we collect, why, how long we keep it, and the rights you have over it. Built on India's DPDP Act 2023 and the EU/UK GDPR.",
    cta: { label: "Read the Privacy Policy", to: "/privacy" },
  },
  {
    icon: Cookie,
    title: "Cookies",
    desc: "A short, plain-English breakdown of every cookie we set — split into Essential, Analytics, and Marketing. You decide which run.",
    cta: { label: "Read the Cookie Notice", to: "/cookies" },
  },
  {
    icon: Shield,
    title: "Security",
    desc: "How we secure your data in transit, at rest, and in our admin tools. Defense layers, retention windows, and the rules our engineers code by.",
    cta: { label: "See security controls", href: "#security" },
  },
];

const SECURITY = [
  {
    icon: Lock,
    title: "Encryption in transit",
    body: "All capexara.com traffic is served over HTTPS with TLS 1.2+ (modern ciphers). HSTS preload candidate. Internal service-to-service calls happen on a private cluster network.",
  },
  {
    icon: Database,
    title: "Encryption at rest",
    body: "Lead records and user content are stored on a managed MongoDB cluster with encryption-at-rest enabled. Backups are encrypted and access-controlled.",
  },
  {
    icon: KeyRound,
    title: "Password handling",
    body: "Admin passwords are never stored in plaintext. We use bcrypt with a work-factor of 12, with the hash held in a server-only secret store (.env), not in any source-control or client bundle.",
  },
  {
    icon: AlertTriangle,
    title: "Brute-force defense",
    body: "Login is rate-limited per (IP, email). After 5 failed attempts the account is locked for 15 minutes and a 429 is returned, blocking credential-stuffing campaigns.",
  },
  {
    icon: ServerCog,
    title: "Secret hygiene",
    body: "API keys, database URIs, admin tokens, and email-provider keys live in environment variables, never in code. Rotation is supported without a redeploy of the bundle.",
  },
  {
    icon: Users,
    title: "Access control",
    body: "Admin endpoints (`/api/admin/*`) require an X-Admin-Token derived from a successful login. The dashboard itself sits behind email + password gating with brute-force lockout.",
  },
];

const RETENTION = [
  { row: "Lead form submissions (name, email, company, message)", value: "24 months · then anonymized" },
  { row: "Calendly meeting metadata", value: "Held by Calendly per their policy" },
  { row: "Admin login attempts (in-memory counters)", value: "Cleared on backend restart, after 15 min, or on success" },
  { row: "Cookie-consent record (browser localStorage)", value: "12 months — re-prompt on policy change" },
  { row: "Analytics identifiers (only if Analytics consent)", value: "Up to 13 months" },
  { row: "Marketing identifiers (only if Marketing consent)", value: "Up to 24 months" },
];

const STANDARDS = [
  { code: "DPDP", label: "Digital Personal Data Protection Act, 2023 (India)" },
  { code: "GDPR", label: "EU/UK General Data Protection Regulation — ready" },
  { code: "TLS 1.2+", label: "Modern transport encryption" },
  { code: "bcrypt", label: "Password storage standard" },
];

export default function Trust() {
  return (
    <section className="legal-page trust-page" data-testid="trust-page">
      <div className="container">
        {/* HERO */}
        <div className="legal-head trust-head">
          <div className="eyebrow"><span>● Trust Center</span></div>
          <h1>How we keep your data, decisions, and platform safe.</h1>
          <p className="legal-lede">
            Capital projects move on trust as much as on capex. This page brings together everything you'd ask
            in a procurement-security review — privacy, cookies, encryption, defense layers, and retention —
            so you can move from evaluation to pilot without chasing PDFs.
          </p>
          <div className="trust-pills">
            {STANDARDS.map((s) => (
              <span key={s.code} className="trust-pill" title={s.label}>
                <span className="trust-pill-code">{s.code}</span>
                <span className="trust-pill-label">{s.label}</span>
              </span>
            ))}
          </div>
        </div>

        {/* THREE PILLARS */}
        <div className="trust-pillars" data-testid="trust-pillars">
          {PILLARS.map((p) => {
            const Icon = p.icon;
            const isInternal = p.cta.to;
            return (
              <div key={p.title} className="trust-pillar">
                <div className="trust-pillar-icon" aria-hidden="true"><Icon size={22} /></div>
                <h3>{p.title}</h3>
                <p>{p.desc}</p>
                {isInternal ? (
                  <Link to={p.cta.to} className="trust-pillar-cta">{p.cta.label} <span className="arrow">→</span></Link>
                ) : (
                  <a href={p.cta.href} className="trust-pillar-cta">{p.cta.label} <span className="arrow">→</span></a>
                )}
              </div>
            );
          })}
        </div>

        {/* SECURITY DETAIL */}
        <div id="security" className="trust-section" data-testid="trust-security">
          <div className="legal-section">
            <div className="trust-section-eyebrow mono">◉ Security</div>
            <h2 style={{ marginTop: 6 }}>Six controls our engineers code by.</h2>
            <p style={{ marginTop: 6 }}>
              Security at Capexara is a default — not a feature. Every product page you see is served by an
              architecture that applies the following layers, audited on every release.
            </p>
          </div>

          <div className="trust-security-grid">
            {SECURITY.map((s) => {
              const Icon = s.icon;
              return (
                <div key={s.title} className="trust-security-card">
                  <div className="trust-security-icon" aria-hidden="true"><Icon size={18} /></div>
                  <div className="trust-security-title">{s.title}</div>
                  <div className="trust-security-body">{s.body}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* RETENTION TABLE */}
        <div className="trust-section">
          <div className="legal-section">
            <div className="trust-section-eyebrow mono">◉ Retention</div>
            <h2 style={{ marginTop: 6 }}>How long we keep things.</h2>
            <p style={{ marginTop: 6 }}>
              We keep data only as long as it's useful — then anonymize or delete. Retention windows below are
              maximums; in practice, data is purged sooner if you ask.
            </p>
            <div className="legal-table-wrap" style={{ marginTop: 18 }}>
              <table className="legal-table">
                <thead><tr><th style={{ width: "60%" }}>What</th><th>How long</th></tr></thead>
                <tbody>
                  {RETENTION.map((r, i) => (
                    <tr key={i}><td>{r.row}</td><td className="mono">{r.value}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* CTA FOOTER */}
        <div className="legal-foot" style={{ marginTop: 32 }}>
          <Link to="/privacy" className="btn btn-ghost">Privacy Policy</Link>
          <Link to="/cookies" className="btn btn-ghost">Cookie Notice</Link>
          <a href="mailto:hello@capexara.com?subject=Security%20review%20questionnaire" className="btn btn-primary">Request a security review pack <span className="arrow">→</span></a>
        </div>
      </div>
    </section>
  );
}
